import { defineStore } from "pinia";
import { $log } from "@/plugins/logger/logger.plugin.js";
import ConfigsService from "@/services/v1/Configs.service";
// import EquipmentService from "@/services/v1/Equipment.service";
// import PropertiesService from "@/services/v1/Properties.service";
import NetworkService from "@/services/v1/Network.service";
import configurationHelpers from "@/helpers/box-configuration.helper.js";

export type ConfigType = null | {
	revisionId: string; // The id of the revision
	deviceId: string; // The device id associated with the revision
	checksum?: string; //The checksum of the revision
	parentRevisionId?: string; // The id of the parent revision
	notes?: string; // A description of the revision
	status: "DRAFT" | "SENT" | "CURRENT" | "DISCARDED" | "ERROR" | "ARCHIVED"; // The status of the revision
	errors?: [string]; // This contains the error messages coming from the device when we deploy the configuration

	properties: any[]; // A list of properties configurations
	equipments: any[]; // A list of equipment configurations
	networks: any[]; // A list of networks configurations
	gatewayInterfaces: any[]; // A list of gateway interfaces configurations

	connectors: {
		[propertyName: string]: any;
	}; // An object containing the configured connectors, the key is the connector name, and the value is its configuration
	pinnedResources: {
		[propertyName: string]: any;
	}; // Resources associated with the revision
	commandId?: string; // The identifier of the command created to send the configuration to the box. Use the Command API to track the status
	embeddedConfig?: object; // The associated embedded configuration
	tags: {
		[key: string]: string;
	}; // The tags of the revision

	scheduler: any; // The associated schedulers configurations
	timeZone: string;

	createdAt?: string; // Creation date
	createdBy?: string; // Creation author
	createdAs?: string; // The impersonated user used for creation
	updatedAt?: string; // Date of the last modification
	updatedBy?: string; // Author of the last modification
	updatedAs?: string; // The impersonated user used for update
};

export const useConfigurationStore = defineStore("configuration", {
	state: () =>
		({
			deviceId: null,
			loaded: false,
			loading: false,
			draftConfiguration: null,
			currentConfiguration: null
		}) as {
			deviceId: string | null;
			loaded: boolean;
			loading: boolean;
			draftConfiguration: ConfigType;
			currentConfiguration: ConfigType;
		},

	getters: {
		hasLoaded: (state) => {
			return state.loaded;
		},
		isLoading: (state) => {
			return state.loading;
		},
		getMergedDraftCurrentProperties: (state) => {
			const finalMerged = configurationHelpers.mergeCurrentAndDraftObject(
				state.currentConfiguration?.properties || [],
				state.draftConfiguration?.properties || [],
				"propertyId"
			);

			return finalMerged;
		}
	},

	actions: {
		reset() {
			this.deviceId = null;
			this.loaded = false;
			this.loading = false;
			this.draftConfiguration = null;
			this.currentConfiguration = null;
		},
		async setDeviceId({ deviceId, forceRefresh = false }) {
			if (this.deviceId === deviceId && !forceRefresh) {
				return;
			}
			this.deviceId = deviceId;
			await this.getAll();
		},
		async getAll() {
			if (this.loading || !this.deviceId) {
				return;
			}
			this.loading = true;
			let draft;
			let current;
			try {
				draft = await ConfigsService.getDraft(this.deviceId);
				current = await ConfigsService.getCurrent(this.deviceId);
				this.draftConfiguration = draft;
				this.currentConfiguration = current;

				this.loading = false;
				this.loaded = true;

				return null;
			} catch (err) {
				$log.error(err);
				this.loading = false;
				throw err;
			}
		},

		async getDraftConfiguration() {
			try {
				this.draftConfiguration = await ConfigsService.getDraft(
					this.deviceId
				);
				return this.draftConfiguration;
			} catch (err) {
				$log.error(err);
				throw err;
			}
		},
		async getCurrentConfiguration() {
			try {
				this.currentConfiguration = await ConfigsService.getCurrent(
					this.deviceId
				);
				return this.currentConfiguration;
			} catch (err) {
				$log.error(err);
				throw err;
			}
		},
		// global config's actions
		publishDraftRevision(notes: string | null) {
			return ConfigsService.publishDraftRevision(notes, this.deviceId);
		},
		async exportDiffAsBlob() {
			return await ConfigsService.exportDiffAsBlob(this.deviceId);
		},

		// equipment's actions
		// properties' actions
		// gateways' actions
		// networks' actions
		async getNetworkFromRevision(
			networkId: string,
			revisionId = "draft",
			deviceId = null
		) {
			return await NetworkService.getFromRevision(
				networkId,
				revisionId,
				deviceId || this.deviceId
			);
		},
		async updateNetwork(networkId: string, network, deviceId = null) {
			try {
				const updatedNetwork = await NetworkService.update(
					networkId,
					network,
					deviceId || this.deviceId
				);
				this.getAll();
				return updatedNetwork;
			} catch (err) {
				$log.error(err);
				throw err;
			}
		}
	}
});
